import { buildTutorialTargetMissingHint } from '../buildTargetMissingHint';
import type { TutorialDefinition, TutorialRecover } from '../types';

const R = {
  page: { path: '/assignment-note', pageLabel: '과제 관리' },
  settings: {
    path: '/assignment-note',
    openSelector: '[aria-label="설정"].cp-apn-header-icon-btn',
    pageLabel: '과제 관리',
    openLabel: '설정',
  },
  generalCreate: {
    path: '/assignment-note',
    openSelector: '[aria-label="일반 과제"] .cp-apn-add-btn',
    pageLabel: '과제 관리',
    openLabel: '+ 과제 생성(일반)',
  },
  coverImage: {
    path: '/assignment-note',
    openSelectors: [
      '[aria-label="일반 과제"] .cp-apn-add-btn',
      '[aria-label="과제 이미지 추가"], [aria-label="과제 이미지 변경"]',
    ],
    pageLabel: '과제 관리',
    openLabel: '과제 이미지',
  },
  projectCoverImage: {
    path: '/assignment-note',
    openSelectors: [
      '[aria-label="프로젝트 과제"] .cp-apn-add-btn',
      '[aria-label="과제 이미지 추가"], [aria-label="과제 이미지 변경"]',
    ],
    pageLabel: '과제 관리',
    openLabel: '과제 이미지',
  },
  routineCoverImage: {
    path: '/assignment-note',
    openSelectors: [
      '[aria-label="루틴 과제"] .cp-apn-add-btn',
      '[aria-label="과제 이미지 추가"], [aria-label="과제 이미지 변경"]',
    ],
    pageLabel: '과제 관리',
    openLabel: '과제 이미지',
  },
  checkModal: {
    path: '/assignment-note',
    openSelector: '.cp-apn-board-cell--general .cp-apn-block-main',
    pageLabel: '과제 관리',
    openLabel: '일반 과제 블럭',
  },
  projectCreate: {
    path: '/assignment-note',
    openSelector: '[aria-label="프로젝트 과제"] .cp-apn-add-btn',
    pageLabel: '과제 관리',
    openLabel: '+ 과제 생성(프로젝트)',
  },
  routineCreate: {
    path: '/assignment-note',
    openSelector: '[aria-label="루틴 과제"] .cp-apn-add-btn',
    pageLabel: '과제 관리',
    openLabel: '+ 과제 생성(루틴)',
  },
  tableModal: {
    path: '/assignment-note',
    openSelector: '[aria-label="과제 관리 테이블"]',
    pageLabel: '과제 관리',
    openLabel: '과제 관리 테이블',
  },
  memberIncomplete: {
    path: '/assignment-note',
    openSelector: '[aria-label="멤버별 미완료 과제"]',
    pageLabel: '과제 관리',
    openLabel: '멤버별 미완료 과제',
  },
  assignmentIncomplete: {
    path: '/assignment-note',
    openSelector: '[aria-label="과제별 미완료 멤버"]',
    pageLabel: '과제 관리',
    openLabel: '과제별 미완료 멤버',
  },
} as const satisfies Record<string, TutorialRecover>;

const RECOVER_BY_STEP_ID: Record<string, TutorialRecover> = {
  'go-assignment-note': R.page,
  'settings-open': R.page,
  'settings-unit': R.settings,
  'settings-status-points': R.settings,
  'settings-categories': R.settings,
  'select-group': R.page,
  'general-create-open': R.page,
  'general-create-basics': R.generalCreate,
  'general-cover-open': R.generalCreate,
  'general-cover-modal': R.coverImage,
  'general-create-reward': R.generalCreate,
  'general-create-fields': R.generalCreate,
  'general-create-members': R.generalCreate,
  'general-create-save': R.generalCreate,
  'general-block-guide': R.page,
  'general-block-open': R.page,
  'check-status': R.checkModal,
  'check-batch-fields': R.checkModal,
  'check-memo-open': R.checkModal,
  'check-memo-panel': R.checkModal,
  'check-kbd-helper': R.checkModal,
  'check-kbd-practice': R.checkModal,
  'check-kbd-auto': R.checkModal,
  'check-print': R.checkModal,
  'project-create-open': R.page,
  'project-create-basics': R.projectCreate,
  'project-cover-open': R.projectCreate,
  'project-cover-modal': R.projectCoverImage,
  'project-create-reward': R.projectCreate,
  'project-create-details': R.projectCreate,
  'project-create-members': R.projectCreate,
  'project-create-save': R.projectCreate,
  'project-block-open': R.page,
  'project-subtasks': R.page,
  'project-reach-btn': R.page,
  'project-reach-modal': R.page,
  'project-reward-btn': R.page,
  'project-reward-modal': R.page,
  'routine-create-open': R.page,
  'routine-create-basics': R.routineCreate,
  'routine-cover-open': R.routineCreate,
  'routine-cover-modal': R.routineCoverImage,
  'routine-create-reward': R.routineCreate,
  'routine-create-schedule': R.routineCreate,
  'routine-create-members': R.routineCreate,
  'routine-create-save': R.routineCreate,
  'routine-block-open': R.page,
  'routine-detail': R.page,
  'routine-reach-btn': R.page,
  'routine-reach-modal': R.page,
  'routine-reward-btn': R.page,
  'routine-reward-modal': R.page,
  'reports-table-open': R.page,
  'reports-table-modal': R.tableModal,
  'reports-member-incomplete-open': R.page,
  'reports-member-incomplete-modal': R.memberIncomplete,
  'reports-assignment-incomplete-open': R.page,
  'reports-assignment-incomplete-modal': R.assignmentIncomplete,
  'layout-mode': R.page,
};

const assignmentNoteRaw: TutorialDefinition = {
  id: 'assignment-note',
  title: '과제 관리',
  summary:
    '설정부터 일반·프로젝트·루틴 과제 만들기와 체크, 미완료 조회·화면 모드까지 과제 관리 흐름을 배워요.',
  steps: [
    {
      id: 'intro',
      mascot:
        '과제 관리 튜토리얼을 시작해요! 설정, 일반·프로젝트·루틴 과제, 체크와 미완료 조회까지 순서대로 알려드릴게요.',
      actionLabel: '시작하기',
      presentation: 'intro',
      cardTitle: '과제 관리를 시작해요!',
      placement: 'center',
    },
    {
      id: 'go-assignment-note',
      mascot: '사이드바에서 "과제 노트"를 눌러 이동해 주세요.',
      targetSelector: 'a[href^="/assignment-note"]',
      placement: 'right',
      section: '[과제 관리] 시작',
      sectionMascot: '과제 관리 페이지로 이동하는 구간이에요.',
    },
    {
      id: 'settings-open',
      mascot: '먼저 오른쪽 위 설정(톱니) 버튼을 눌러 주세요.',
      targetSelector: '[aria-label="설정"].cp-apn-header-icon-btn',
      section: '[과제 관리] 설정',
      sectionMascot: '기본 포인트·상태별 비율·카테고리를 설정하는 구간이에요.',
    },
    {
      id: 'settings-unit',
      mascot:
        '"기본 단위 포인트"는 과제에 기본으로 쓰일 포인트 단위예요. 확인했으면 "다음"을 눌러 주세요.',
      targetSelector: 'text:기본 단위 설정@.cp-apn-set-pill-title, .cp-apn-set-modal .cp-apn-set-unit-btn',
      manualAdvance: true,
    },
    {
      id: 'settings-status-points',
      mascot:
        '"과제 상태별 기본 지급 포인트"에서 확인·지연확인·예외 등 상태마다 지급 비율을 정할 수 있어요. 확인했으면 "다음"을 눌러 주세요.',
      targetSelector: '.cp-apn-set-modal .cp-apn-set-status-grid',
      manualAdvance: true,
    },
    {
      id: 'settings-categories',
      mascot:
        '"과제 카테고리 설정"에서 과제를 분류할 카테고리를 만들 수 있어요. 살펴본 뒤 모달을 닫거나 "다음"을 눌러 주세요.',
      targetSelector: 'text:과제 카테고리 설정@.cp-apn-set-pill-title, .cp-apn-set-modal',
      advanceWhenSelectorGone: '.cp-apn-set-modal',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'select-group',
      mascot:
        '과제 목록·체크는 선택한 멤버 그룹 기준으로 보여요. 위쪽에서 그룹을 골라 주세요. 고른 뒤 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-apn="group-select"]',
      manualAdvance: true,
      section: '[과제 관리] 그룹 선택',
      sectionMascot: '과제를 보고 만들 멤버 그룹을 고르는 구간이에요.',
    },
    {
      id: 'general-create-open',
      mascot: '일반 과제 섹션의 "+ 과제 생성"을 눌러 주세요.',
      targetSelector: '[aria-label="일반 과제"] .cp-apn-add-btn',
      section: '[일반 과제] 만들기',
      sectionMascot: '일반 과제를 만들고 이미지·보상·멤버를 설정하는 구간이에요.',
    },
    {
      id: 'general-create-basics',
      mascot:
        '과제 이름·설명과 과제 이미지 칸이 있는 첫 섹션이에요. 이름을 입력해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-apn-create-modal[aria-label="일반 과제 생성"] [data-tutorial-apn-create="basics"]',
      manualAdvance: true,
    },
    {
      id: 'general-cover-open',
      mascot: '과제 이미지 칸을 눌러 이미지 설정 창을 열어 주세요.',
      targetSelector:
        '.cp-apn-create-modal[aria-label="일반 과제 생성"] [aria-label="과제 이미지 추가"], .cp-apn-create-modal[aria-label="일반 과제 생성"] [aria-label="과제 이미지 변경"], .cp-apn-create-modal[aria-label="일반 과제 생성"] .cp-apn-create-cover-slot',
    },
    {
      id: 'general-cover-modal',
      mascot:
        '과제 이미지 설정 창이에요. 실제 이미지를 넣어 보세요.\n· 파일을 드래그 앤 드롭하거나\n· 이미지를 캡처한 뒤 Ctrl+V로 붙여 넣을 수 있어요.\n이미지를 넣은 뒤 슬라이드로 크기를 조정하고 잘라 볼 수 있어요. 등록하거나 창을 닫으면 다음으로 이어져요. 건너뛰려면 "다음"을 눌러 주세요.',
      targetSelector: '.cp-apn-cover-upload-modal, .cp-member-photo-upload-modal.cp-apn-cover-upload-modal',
      advanceWhenSelectorGone:
        '.cp-apn-cover-upload-modal, .cp-member-photo-upload-modal.cp-apn-cover-upload-modal',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'general-create-reward',
      mascot:
        '"과제 완료 보상"을 ON으로 켠 뒤, 펼쳐진 섹션에서 획득 코인을 설정해 보세요. ON이 되고 코인까지 확인했으면 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-apn-create-modal[aria-label="일반 과제 생성"] [data-tutorial-apn-create="reward"]',
      manualAdvance: true,
      manualAdvanceEnableWhen:
        '.cp-apn-create-modal[aria-label="일반 과제 생성"] [data-tutorial-apn-create="reward"] .cp-apn-create-toggle.is-on',
    },
    {
      id: 'general-create-fields',
      mascot:
        '마감 날짜·획득 포인트·카테고리 섹션이에요. 원하는 대로 조정해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-apn-create-modal[aria-label="일반 과제 생성"] [data-tutorial-apn-create="schedule"]',
      manualAdvance: true,
    },
    {
      id: 'general-create-members',
      mascot:
        '"과제를 부여할 멤버"에서 대상 멤버·그룹을 골라 주세요. 여러 그룹을 함께 고르면 그 그룹들이 하나의 과제를 공유하는 공유과제가 돼요. 멤버를 고른 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-apn-create-modal[aria-label="일반 과제 생성"] [data-tutorial-apn-create="members"]',
      manualAdvance: true,
    },
    {
      id: 'general-create-save',
      mascot:
        '설정을 마쳤으면 "저장"을 눌러 일반 과제를 만들어 주세요. 취소나 바깥 클릭으로는 닫을 수 없고, 저장해야 다음으로 넘어가요.',
      targetSelector: '.cp-apn-create-modal[aria-label="일반 과제 생성"]',
      advanceSelector:
        '.cp-apn-create-modal[aria-label="일반 과제 생성"] .cp-apn-create-btn-save',
      blockClickSelectors: '.cp-apn-create-btn-cancel',
    },
    {
      id: 'general-block-guide',
      mascot:
        '만들어진 일반 과제 블럭을 확인해 보세요. 마감일·포인트·미확인 인원 표시가 보여요. 확인했으면 "다음"을 눌러 주세요.',
      targetSelector: '.cp-apn-board-cell--general .cp-apn-block',
      manualAdvance: true,
      blockTargetClick: true,
      section: '[일반 과제] 체크하기',
      sectionMascot: '과제 블럭을 열고 상태·메모·키보드 도우미로 체크하는 구간이에요.',
      targetMissingHint:
        '일반 과제 블럭이 아직 없어요. 앞에서 "+ 과제 생성"으로 일반 과제를 저장한 뒤 다시 진행해 주세요.',
    },
    {
      id: 'general-block-open',
      mascot: '일반 과제 블럭을 클릭해 과제 체크 창을 열어 주세요.',
      targetSelector: '.cp-apn-board-cell--general .cp-apn-block-main',
    },
    {
      id: 'check-status',
      mascot:
        '과제 상태는 네 가지예요.\n· 미확인: 아직 과제 확인이 안 된 상태\n· 확인: 과제 확인이 된 상태\n· 지연확인: 마감일이 지난 뒤 확인한 상태(보상은 설정에 따라 확인과 같거나 차등)\n· 예외: 확인한 것으로 예외 처리(보상도 설정에 따라 확인과 같거나 차등)\n상태 버튼을 눌러 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '.cp-apn-check-modal-panel [aria-label="과제 상태"], .cp-apn-check-modal-panel .cp-apn-status-btn',
      manualAdvance: true,
    },
    {
      id: 'check-batch-fields',
      mascot:
        '상태 버튼 아래 포인트·메모 입력란은 선택한 멤버 블럭에 한꺼번에 넣을 수 있는 필드예요. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '.cp-apn-check-modal-panel .cp-apn-points-field, .cp-apn-check-modal-panel .cp-apn-memo-field',
      manualAdvance: true,
    },
    {
      id: 'check-memo-open',
      mascot: '"메모 열기"를 눌러 멤버별 메모 영역을 열어 주세요.',
      targetSelector: 'text:메모 열기@.cp-apn-mini-btn',
      advanceWhenSelectorExists: 'text:메모 닫기@.cp-apn-mini-btn',
      manualAdvanceIfExists: 'text:메모 닫기@.cp-apn-mini-btn',
    },
    {
      id: 'check-memo-panel',
      mascot: '각 멤버에 대한 메모를 기록할 수 있어요. 확인했으면 "다음"을 눌러 주세요.',
      targetSelector: '.cp-apn-check-modal-panel',
      manualAdvance: true,
    },
    {
      id: 'check-kbd-helper',
      mascot: '"키보드 과제 체크 도우미"를 열어 주세요.',
      targetSelector: '.cp-apn-kbd-toggle',
      advanceWhenSelectorExists: '.cp-apn-kbd-helper',
      manualAdvanceIfExists: '.cp-apn-kbd-helper',
    },
    {
      id: 'check-kbd-practice',
      mascot:
        '대상 필드에 학생 번호를 입력하고, 과제 상태를 바꾼 뒤 "입력"을 눌러 적용해 보세요. 한 번 입력에 성공하면 다음으로 이어져요.',
      targetSelector: '.cp-apn-kbd-fields, .cp-apn-kbd-actions',
      spotlightUnion: true,
      advanceWhenSelectorExists: '.cp-apn-kbd-helper[data-tutorial-apn-kbd="practiced"]',
    },
    {
      id: 'check-kbd-auto',
      mascot:
        '"연속 번호 입력하기"를 켜 두면, 대상에 입력한 학생의 다음 번호가 자동으로 선택돼요. 확인해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: 'text:연속 번호 입력하기@.cp-apn-kbd-opt, .cp-apn-kbd-helper .cp-apn-switch',
      manualAdvance: true,
    },
    {
      id: 'check-print',
      mascot:
        '프린트 버튼으로 과제 상태와 메모를 출력할 수 있어요. 위치를 확인한 뒤 창을 닫거나 "다음"을 눌러 주세요.',
      targetSelector: '.cp-apn-check-head-actions [aria-label="프린트"], .cp-apn-check-modal-panel',
      advanceWhenSelectorGone: '.cp-apn-check-modal-panel',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'project-create-open',
      mascot: '이번엔 프로젝트 과제예요. 프로젝트 과제 섹션의 "+ 과제 생성"을 눌러 주세요.',
      targetSelector: '[aria-label="프로젝트 과제"] .cp-apn-add-btn',
      section: '[프로젝트 과제] 만들기',
      sectionMascot: '하위 과제·보상 아이템·색상이 있는 프로젝트 과제를 만드는 구간이에요.',
    },
    {
      id: 'project-create-basics',
      mascot:
        '과제 이름·설명과 과제 이미지 칸이 있는 첫 섹션이에요. 이름을 입력해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-apn-create-modal[aria-label="프로젝트 과제 생성"] [data-tutorial-apn-create="basics"]',
      manualAdvance: true,
    },
    {
      id: 'project-cover-open',
      mascot: '과제 이미지 칸을 눌러 이미지 설정 창을 열어 주세요.',
      targetSelector:
        '.cp-apn-create-modal[aria-label="프로젝트 과제 생성"] [aria-label="과제 이미지 추가"], .cp-apn-create-modal[aria-label="프로젝트 과제 생성"] [aria-label="과제 이미지 변경"], .cp-apn-create-modal[aria-label="프로젝트 과제 생성"] .cp-apn-create-cover-slot',
    },
    {
      id: 'project-cover-modal',
      mascot:
        '과제 이미지 설정 창이에요. 이미지를 넣거나 창을 닫으면 다음으로 이어져요. 건너뛰려면 "다음"을 눌러 주세요.',
      targetSelector: '.cp-apn-cover-upload-modal, .cp-member-photo-upload-modal.cp-apn-cover-upload-modal',
      advanceWhenSelectorGone:
        '.cp-apn-cover-upload-modal, .cp-member-photo-upload-modal.cp-apn-cover-upload-modal',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'project-create-reward',
      mascot:
        '"프로젝트 완료 보상"을 ON으로 켠 뒤, 획득 코인과 보상 아이템(카드)을 설정해 보세요. ON을 켠 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-apn-create-modal[aria-label="프로젝트 과제 생성"] [data-tutorial-apn-create="reward"]',
      manualAdvance: true,
      manualAdvanceEnableWhen:
        '.cp-apn-create-modal[aria-label="프로젝트 과제 생성"] [data-tutorial-apn-create="reward"] .cp-apn-create-toggle.is-on',
    },
    {
      id: 'project-create-details',
      mascot:
        '마감 날짜·하위 과제·프로젝트 색상 섹션이에요. 하위 과제를 추가·수정하고 색상도 골라 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-apn-create-modal[aria-label="프로젝트 과제 생성"] [data-tutorial-apn-create="details"]',
      manualAdvance: true,
    },
    {
      id: 'project-create-members',
      mascot:
        '"과제를 부여할 멤버"에서 대상 멤버·그룹을 골라 주세요. 멤버를 고른 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-apn-create-modal[aria-label="프로젝트 과제 생성"] [data-tutorial-apn-create="members"]',
      manualAdvance: true,
    },
    {
      id: 'project-create-save',
      mascot:
        '설정을 마쳤으면 "저장"을 눌러 프로젝트 과제를 만들어 주세요. 취소나 바깥 클릭으로는 닫을 수 없고, 저장해야 다음으로 넘어가요.',
      targetSelector: '.cp-apn-create-modal[aria-label="프로젝트 과제 생성"]',
      advanceSelector:
        '.cp-apn-create-modal[aria-label="프로젝트 과제 생성"] .cp-apn-create-btn-save',
      blockClickSelectors: '.cp-apn-create-btn-cancel',
    },
    {
      id: 'project-block-open',
      mascot:
        '프로젝트 과제 블럭을 클릭하면 하위 과제가 펼쳐져요. 블럭을 눌러 열어 주세요.\n블럭이 없으면 앞에서 프로젝트 과제를 저장한 뒤 다시 진행해 주세요.',
      targetSelector: '.cp-apn-board-cell--project .cp-apn-block-main',
      advanceWhenSelectorExists: '.cp-apn-board-cell--project .cp-apn-detail',
      manualAdvanceIfExists: '.cp-apn-board-cell--project .cp-apn-detail',
      section: '[프로젝트 과제] 확인하기',
      sectionMascot: '하위 과제와 멤버별 도달률을 확인하는 구간이에요.',
    },
    {
      id: 'project-subtasks',
      mascot:
        '펼쳐진 프로젝트 상세예요. 위쪽에서는 멤버별 도달률·보상 지급을 할 수 있고, "하위과제 현황"에서 각 하위 과제의 포인트와 미확인 인원을 볼 수 있어요. 지금은 살펴만 보고 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-apn="project-detail"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'project-reach-btn',
      mascot: '"멤버별 도달률"을 눌러 주세요.',
      targetSelector: '[data-tutorial-apn="project-reach"]',
      // 모달이 열리면 클릭 진행이 누락돼도 다음으로 넘어간다.
      advanceWhenSelectorExists: '.cp-apn-reach-modal',
    },
    {
      id: 'project-reach-modal',
      mascot:
        '멤버별로 프로젝트 하위 과제 도달률을 볼 수 있어요. 살펴본 뒤 모달을 닫거나 "다음"을 눌러 주세요.',
      targetSelector: '.cp-apn-reach-modal',
      advanceSelector: '.cp-apn-reach-modal .cp-apn-create-btn-cancel',
      advanceWhenSelectorGone: '.cp-apn-reach-modal',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'project-reward-btn',
      mascot:
        '"프로젝트 과제 보상 지급"을 눌러 주세요. 도달률을 충족한 멤버에게 코인·아이템 보상을 지급하고, 조건을 못 채운 멤버에게는 회수할 수 있어요.\n완료 보상을 켜지 않아 버튼이 없거나, 눌러도 창이 열리지 않으면 "다음"으로 넘어가 주세요.',
      targetSelector: '[data-tutorial-apn="project-reward"]',
      advanceWhenSelectorExists: '.cp-apn-reward-result-modal',
      manualAdvance: true,
      // 결과 모달을 열지 않고 건너뛸 때는 모달 안내도 같이 건너뛴다.
      manualAdvanceSkipToStepId: 'routine-create-open',
      targetMissingHint:
        '보상 지급 버튼이 없어요. 프로젝트 생성 시 "프로젝트 완료 보상"을 ON으로 켜야 버튼이 나타나요. 지금은 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'project-reward-modal',
      mascot:
        '보상 처리 결과 창이에요. 이번에 지급·회수된 멤버와 현재 보상 지급 완료 멤버를 확인할 수 있어요. 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '.cp-apn-reward-result-modal',
      manualAdvance: true,
      blockTargetClick: true,
      targetMissingHint:
        '보상 결과 창이 열려 있지 않아요. "다음"을 눌러 이어서 진행해 주세요.',
    },
    {
      id: 'routine-create-open',
      mascot: '루틴 과제 섹션의 "+ 과제 생성"을 눌러 주세요.',
      targetSelector: '[aria-label="루틴 과제"] .cp-apn-add-btn',
      section: '[루틴 과제] 만들기',
      sectionMascot: '반복 요일·도달률이 있는 루틴 과제를 만드는 구간이에요.',
    },
    {
      id: 'routine-create-basics',
      mascot:
        '과제 이름·설명과 과제 이미지 칸이 있는 첫 섹션이에요. 이름을 입력해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-apn-create-modal[aria-label="루틴 과제 생성"] [data-tutorial-apn-create="basics"]',
      manualAdvance: true,
    },
    {
      id: 'routine-cover-open',
      mascot: '과제 이미지 칸을 눌러 이미지 설정 창을 열어 주세요.',
      targetSelector:
        '.cp-apn-create-modal[aria-label="루틴 과제 생성"] [aria-label="과제 이미지 추가"], .cp-apn-create-modal[aria-label="루틴 과제 생성"] [aria-label="과제 이미지 변경"], .cp-apn-create-modal[aria-label="루틴 과제 생성"] .cp-apn-create-cover-slot',
    },
    {
      id: 'routine-cover-modal',
      mascot:
        '과제 이미지 설정 창이에요. 이미지를 넣거나 창을 닫으면 다음으로 이어져요. 건너뛰려면 "다음"을 눌러 주세요.',
      targetSelector: '.cp-apn-cover-upload-modal, .cp-member-photo-upload-modal.cp-apn-cover-upload-modal',
      advanceWhenSelectorGone:
        '.cp-apn-cover-upload-modal, .cp-member-photo-upload-modal.cp-apn-cover-upload-modal',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'routine-create-reward',
      mascot:
        '"루틴 과제 완료 보상"을 ON으로 켠 뒤, 획득 코인·보상 카드·과제 완료 도달률을 설정해 보세요. ON을 켠 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-apn-create-modal[aria-label="루틴 과제 생성"] [data-tutorial-apn-create="reward"]',
      manualAdvance: true,
      manualAdvanceEnableWhen:
        '.cp-apn-create-modal[aria-label="루틴 과제 생성"] [data-tutorial-apn-create="reward"] .cp-apn-create-toggle.is-on',
    },
    {
      id: 'routine-create-schedule',
      mascot:
        '반복 요일·시작/마감 날짜·획득 포인트 섹션이에요. 원하는 대로 조정해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-apn-create-modal[aria-label="루틴 과제 생성"] [data-tutorial-apn-create="schedule"]',
      manualAdvance: true,
    },
    {
      id: 'routine-create-members',
      mascot:
        '"과제를 부여할 멤버"에서 대상 멤버·그룹을 골라 주세요. 멤버를 고른 뒤 "다음"을 눌러 주세요.',
      targetSelector:
        '.cp-apn-create-modal[aria-label="루틴 과제 생성"] [data-tutorial-apn-create="members"]',
      manualAdvance: true,
    },
    {
      id: 'routine-create-save',
      mascot:
        '설정을 마쳤으면 "저장"을 눌러 루틴 과제를 만들어 주세요. 취소나 바깥 클릭으로는 닫을 수 없고, 저장해야 다음으로 넘어가요.',
      targetSelector: '.cp-apn-create-modal[aria-label="루틴 과제 생성"]',
      advanceSelector:
        '.cp-apn-create-modal[aria-label="루틴 과제 생성"] .cp-apn-create-btn-save',
      blockClickSelectors: '.cp-apn-create-btn-cancel',
    },
    {
      id: 'routine-block-open',
      mascot:
        '루틴 과제 블럭을 클릭해 날짜별 현황을 열어 주세요.\n블럭이 없으면 앞에서 루틴 과제를 저장한 뒤 다시 진행해 주세요.',
      targetSelector: '.cp-apn-board-cell--routine .cp-apn-block-main',
      advanceWhenSelectorExists: '[data-tutorial-apn="routine-detail"]',
      manualAdvanceIfExists: '[data-tutorial-apn="routine-detail"]',
      section: '[루틴 과제] 확인하기',
      sectionMascot: '날짜별 현황·멤버별 도달률·보상 지급을 알아보는 구간이에요.',
    },
    {
      id: 'routine-detail',
      mascot:
        '날짜별 과제 현황이에요.\n· 미래 날짜는 표시되지 않아요.\n· 루틴 과제는 미확인 하위 과제가 있어도 미완료로 보지 않아요.\n· 루틴 요일이 아닌 날에도 기록하려면 "커스텀 날짜 등록"으로 날짜를 추가할 수 있어요.\n지금은 살펴만 보고 "다음"을 눌러 주세요.',
      targetSelector: '[data-tutorial-apn="routine-detail"]',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'routine-reach-btn',
      mascot: '"멤버별 도달률"을 눌러 주세요.',
      targetSelector: '[data-tutorial-apn="routine-reach"]',
      advanceWhenSelectorExists: '.cp-apn-reach-modal',
    },
    {
      id: 'routine-reach-modal',
      mascot:
        '멤버별로 루틴 과제 도달률을 볼 수 있어요. 살펴본 뒤 모달을 닫거나 "다음"을 눌러 주세요.',
      targetSelector: '.cp-apn-reach-modal',
      advanceSelector: '.cp-apn-reach-modal .cp-apn-create-btn-cancel',
      advanceWhenSelectorGone: '.cp-apn-reach-modal',
      manualAdvance: true,
      allowManualAdvanceActions: true,
    },
    {
      id: 'routine-reward-btn',
      mascot:
        '"보상 지급"을 눌러 주세요. 도달률을 충족한 멤버에게 코인·아이템 보상을 지급하고, 조건을 못 채운 멤버에게는 회수할 수 있어요.\n완료 보상을 켜지 않아 버튼이 없거나, 눌러도 창이 열리지 않으면 "다음"으로 넘어가 주세요.',
      targetSelector: '[data-tutorial-apn="routine-reward"]',
      advanceWhenSelectorExists: '.cp-apn-reward-result-modal',
      manualAdvance: true,
      manualAdvanceSkipToStepId: 'reports-table-open',
      targetMissingHint:
        '보상 지급 버튼이 없어요. 루틴 과제 생성 시 "루틴 과제 완료 보상"을 ON으로 켜야 버튼이 나타나요. 지금은 "다음"으로 넘어가도 돼요.',
    },
    {
      id: 'routine-reward-modal',
      mascot:
        '보상 처리 결과 창이에요. 이번에 지급·회수된 멤버와 현재 보상 지급 완료 멤버를 확인할 수 있어요. 살펴본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '.cp-apn-reward-result-modal',
      manualAdvance: true,
      blockTargetClick: true,
      targetMissingHint:
        '보상 결과 창이 열려 있지 않아요. "다음"을 눌러 이어서 진행해 주세요.',
    },
    {
      id: 'reports-table-open',
      mascot: '상단의 "과제 관리 테이블" 버튼을 눌러 주세요.',
      targetSelector: '[aria-label="과제 관리 테이블"]',
      section: '[과제 관리] 테이블·미완료 조회',
      sectionMascot: '테이블과 미완료 현황을 한눈에 보는 구간이에요.',
    },
    {
      id: 'reports-table-modal',
      mascot:
        '과제 관리 테이블에서 과제·상태를 표로 모아 볼 수 있어요. 지금은 모달만 살펴보고 "다음"을 눌러 주세요.',
      targetSelector: '.cp-apn-tbl-modal',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'reports-member-incomplete-open',
      mascot: '"멤버별 미완료 과제" 버튼을 눌러 주세요.',
      targetSelector: '[aria-label="멤버별 미완료 과제"]',
    },
    {
      id: 'reports-member-incomplete-modal',
      mascot:
        '멤버별로 아직 끝내지 않은 과제를 모아 볼 수 있어요. 지금은 모달만 살펴보고 "다음"을 눌러 주세요.',
      targetSelector: '.cp-apn-incomplete-modal',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'reports-assignment-incomplete-open',
      mascot: '"과제별 미완료 멤버" 버튼을 눌러 주세요.',
      targetSelector: '[aria-label="과제별 미완료 멤버"]',
    },
    {
      id: 'reports-assignment-incomplete-modal',
      mascot:
        '과제별로 아직 완료하지 않은 멤버를 모아 볼 수 있어요. 지금은 모달만 살펴보고 "다음"을 눌러 주세요.',
      targetSelector: '.cp-apn-incomplete-modal',
      manualAdvance: true,
      blockTargetClick: true,
    },
    {
      id: 'layout-mode',
      mascot:
        '가로모드·세로모드로 과제 블럭 배치를 바꿀 수 있어요. 전환해 본 뒤 "다음"을 눌러 주세요.',
      targetSelector: '.cp-todo-note-view-toggle[aria-label="과제 블럭 표시 모드"]',
      manualAdvance: true,
      section: '[과제 관리] 가로·세로 모드',
      sectionMascot: '과제 블럭 표시 방향을 바꾸는 구간이에요.',
    },
    {
      id: 'outro',
      mascot:
        '과제 관리 튜토리얼을 마쳤어요. 필요할 때 계정 메뉴의 "튜토리얼"에서 다시 볼 수 있어요.',
      actionLabel: '완료',
      presentation: 'celebration',
      cardTitle: '잘하셨어요!',
      placement: 'center',
    },
  ],
};

const assignmentNote: TutorialDefinition = {
  ...assignmentNoteRaw,
  steps: assignmentNoteRaw.steps.map((step) => {
    const recover = RECOVER_BY_STEP_ID[step.id];
    if (!recover && !step.targetMissingHint) return step;
    const targetMissingHint = buildTutorialTargetMissingHint(recover, step.targetMissingHint);
    return {
      ...step,
      ...(recover ? { recover } : {}),
      targetMissingHint,
    };
  }),
};

export default assignmentNote;
